/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business 汎用複数選択 Picker。popover 型のコンボボックスで、選択値を chip 列で表示する。
 *  タグ・カテゴリ等の複数選択 UI で使う。単一選択は Picker を使うこと
 * @graph-connects none
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useFloatingPosition } from '../../../hooks/useFloatingPosition';
import { cn } from '../../../lib/cn';

export interface MultiPickerOption {
  /** 識別子 (onChange の配列に含まれる) */
  value: string;
  /** 表示ラベル */
  label: string;
  /** 補助説明 (行の 2 行目に出る) */
  description?: string;
  /** 選択不能 */
  disabled?: boolean;
}

export interface MultiPickerProps {
  /** 候補一覧 */
  options: readonly MultiPickerOption[];
  /** 選択中の value 配列 */
  value: readonly string[];
  /** 選択変更ハンドラ */
  onChange: (value: string[]) => void;
  /** 未選択時の placeholder */
  placeholder?: string;
  /** ラベル (上に小さく表示) */
  label?: string;
  /** 検索フォームを表示する (default: true) */
  searchable?: boolean;
  /** 検索フォームの placeholder */
  searchPlaceholder?: string;
  /** 検索ヒット 0 件時の文言 */
  emptyMessage?: string;
  /** 全体を無効化 */
  disabled?: boolean;
  /** トリガー要素の id (label の for と紐づけ) */
  id?: string;
  /** ARIA ラベル (label prop が無い時に必須) */
  'aria-label'?: string;
  /** 追加 className (ルート要素) */
  className?: string;
}

/**
 * 汎用 MultiPicker。
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business 複数選択ドロップダウン。chip 表示・検索・キーボード操作に対応する
 * @graph-connects none
 */
export function MultiPicker({
  options,
  value,
  onChange,
  placeholder = '選択してください',
  label,
  searchable = true,
  searchPlaceholder = '検索...',
  emptyMessage = '候補がありません',
  disabled = false,
  id,
  'aria-label': ariaLabel,
  className,
}: MultiPickerProps): ReactNode {
  const autoId = useId();
  const triggerId = id ?? `cortex-mpk-${autoId}`;
  const listboxId = `${triggerId}-listbox`;
  const labelId = label ? `${triggerId}-label` : undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const popoverRect = useFloatingPosition(triggerRef, open, popoverRef);

  const filtered = useMemo(() => {
    if (!searchable) return [...options];
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [...options];
    return options.filter((o) =>
      [o.label, o.description ?? '', o.value].join(' ').toLowerCase().includes(trimmed),
    );
  }, [options, query, searchable]);

  const optionByValue = useMemo(() => {
    const map = new Map<string, MultiPickerOption>();
    for (const option of options) map.set(option.value, option);
    return map;
  }, [options]);

  // open 切り替え時に active index を整える
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    const firstEnabled = filtered.findIndex((o) => !o.disabled);
    setActiveIndex(firstEnabled);
  }, [open, filtered]);

  // 外側クリックで閉じる (popover は portal なので popoverRef も判定に含める)
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !searchable) return;
    searchRef.current?.focus();
  }, [open, searchable]);

  const toggleValue = useCallback(
    (optionValue: string) => {
      const exists = value.includes(optionValue);
      onChange(exists ? value.filter((v) => v !== optionValue) : [...value, optionValue]);
    },
    [value, onChange],
  );

  const toggleIndex = useCallback(
    (idx: number) => {
      const opt = filtered[idx];
      if (!opt || opt.disabled) return;
      toggleValue(opt.value);
    },
    [filtered, toggleValue],
  );

  const removeValue = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>, optionValue: string) => {
      event.stopPropagation();
      onChange(value.filter((v) => v !== optionValue));
    },
    [value, onChange],
  );

  const moveActive = (direction: 1 | -1) => {
    if (filtered.length === 0) {
      setActiveIndex(-1);
      return;
    }
    let next = activeIndex;
    const start = activeIndex < 0 ? (direction === 1 ? -1 : 0) : activeIndex;
    for (let i = 1; i <= filtered.length; i += 1) {
      const candidate = (start + direction * i + filtered.length) % filtered.length;
      if (!filtered[candidate]?.disabled) {
        next = candidate;
        break;
      }
    }
    setActiveIndex(next);
    optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
  };

  const onTriggerKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onPopoverKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      toggleIndex(activeIndex);
    }
  };

  const popover = open ? (
    <div
      ref={popoverRef}
      className="cortex-mp-popover"
      role="presentation"
      style={{
        position: 'fixed',
        top: popoverRect?.top ?? 0,
        left: popoverRect?.left ?? 0,
        width: popoverRect?.width ?? 'auto',
        // 上下どちらにも収まらない時は内部スクロールに切り替える（画面外へ出さない）
        maxHeight: popoverRect ? `${String(popoverRect.maxHeight)}px` : undefined,
        visibility: popoverRect ? 'visible' : 'hidden',
      }}
      onKeyDown={onPopoverKey}
    >
      {searchable ? (
        <div className="cortex-mp-search">
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder={searchPlaceholder}
            className="cortex-mp-search-input"
            aria-controls={listboxId}
          />
        </div>
      ) : null}
      {filtered.length === 0 ? (
        <p className="cortex-mp-empty">{emptyMessage}</p>
      ) : (
        <ul id={listboxId} role="listbox" aria-multiselectable className="cortex-mp-listbox">
          {filtered.map((opt, idx) => {
            const isSelected = value.includes(opt.value);
            const isActive = idx === activeIndex;
            return (
              <li
                key={opt.value}
                ref={(el) => {
                  optionRefs.current[idx] = el;
                }}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                data-active={isActive}
                data-selected={isSelected}
                className="cortex-mp-option"
                onMouseEnter={() => {
                  setActiveIndex(idx);
                }}
                onClick={() => {
                  toggleIndex(idx);
                }}
              >
                <span className="cortex-mp-option-main">
                  <span className="cortex-mp-option-name">{opt.label}</span>
                  {opt.description !== undefined && opt.description !== '' ? (
                    <span className="cortex-mp-option-description">{opt.description}</span>
                  ) : null}
                </span>
                {isSelected ? (
                  <svg
                    className="cortex-mp-option-check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={cn('cortex-mp-root', className)}>
      {label ? (
        <span id={labelId} className="cortex-mp-label">
          {label}
        </span>
      ) : null}
      <div
        ref={triggerRef}
        id={triggerId}
        className="cortex-mp-trigger"
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-label={labelId === undefined ? ariaLabel : undefined}
        aria-disabled={disabled}
        data-open={open ? 'true' : 'false'}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        onKeyDown={onTriggerKey}
      >
        <span className="cortex-mp-trigger-content">
          {value.length === 0 ? (
            <span className="cortex-mp-placeholder">{placeholder}</span>
          ) : (
            value.map((v) => {
              const label2 = optionByValue.get(v)?.label ?? v;
              return (
                <span key={v} className="cortex-mp-chip" title={label2}>
                  <span className="cortex-mp-chip-name">{label2}</span>
                  {!disabled ? (
                    <button
                      type="button"
                      className="cortex-mp-chip-remove"
                      aria-label={`${label2} を選択解除`}
                      onClick={(e) => {
                        removeValue(e, v);
                      }}
                    >
                      ×
                    </button>
                  ) : null}
                </span>
              );
            })
          )}
        </span>
        <svg
          className="cortex-mp-caret"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
